/**
 * SVG → PNG 海报渲染工具
 */
import mbtiSvg from '../templates/mbti-poster.svg?raw'
import sbtiSvg from '../templates/sbti-poster.svg?raw'
import inviteSvg from '../templates/invite-poster.svg?raw'
import agentSvg from '../templates/agent-poster.svg?raw'
import appShareSvg from '../templates/app-share-poster.svg?raw'
import namingSvg from '../templates/naming-poster.svg?raw'
import type { PosterType } from '../types'

const templateMap: Record<PosterType, string> = {
  mbti: mbtiSvg,
  sbti: sbtiSvg,
  invite: inviteSvg,
  agent: agentSvg,
  app: appShareSvg,
  naming: namingSvg,
}

/**
 * 替换 SVG 模板中的 {{placeholder}} 占位符
 * 自动为 MBTI / Agent 的 BarWidth 字段计算像素宽度
 */
function replacePlaceholders(svg: string, data: Record<string, string>): string {
  const computed: Record<string, string> = { ...data }

  // MBTI 维度条宽度（轨道 580px）
  const barMax = 580
  for (const key of ['ei', 'sn', 'tf', 'jp']) {
    const pctKey = `${key}Pct`
    const barKey = `${key}BarWidth`
    if (svg.includes(`{{${barKey}}}`) && computed[pctKey]) {
      computed[barKey] = String(Math.round(barMax * Number(computed[pctKey]) / 100))
    }
  }

  // Agent Big Five 条宽度（轨道 580px）
  for (const key of ['openness', 'conscientiousness', 'extraversion', 'agreeableness', 'neuroticism']) {
    const barKey = `${key}BarWidth`
    if (svg.includes(`{{${barKey}}}`) && computed[key]) {
      computed[barKey] = String(Math.round(barMax * Number(computed[key]) / 100))
    }
  }

  return svg.replace(/\{\{(\w+)\}\}/g, (match, key) => {
    return computed[key] ?? match
  })
}

/**
 * 将 SVG 中的外部图片 URL 转为 base64 data URI
 * （Canvas 跨域安全要求）
 */
async function inlineImages(svg: string): Promise<string> {
  const imgRegex = /<image[^>]+href="([^"]+)"[^>]*\/?>/g
  const matches = [...svg.matchAll(imgRegex)]

  for (const match of matches) {
    const url = match[1]
    if (!url || url.startsWith('data:')) continue
    try {
      const resp = await fetch(url)
      const blob = await resp.blob()
      const base64 = await new Promise<string>((resolve) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(reader.result as string)
        reader.readAsDataURL(blob)
      })
      svg = svg.replace(match[0], match[0].replace(url, base64))
    } catch {
      // 图片加载失败保持原样
    }
  }
  return svg
}

/**
 * SVG 字符串 → PNG data URL
 */
async function svgToPng(svgString: string, width = 750, height = 1334): Promise<string> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas')
    canvas.width = width * 2
    canvas.height = height * 2
    const ctx = canvas.getContext('2d')!

    const img = new Image()
    img.crossOrigin = 'anonymous'
    const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' })
    const url = URL.createObjectURL(blob)

    img.onload = () => {
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
      URL.revokeObjectURL(url)
      resolve(canvas.toDataURL('image/png'))
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('SVG 渲染失败'))
    }
    img.src = url
  })
}

/**
 * 主渲染函数：模板 + 数据 → PNG
 */
export async function renderPoster(
  type: PosterType,
  data: Record<string, string>
): Promise<{ svgHtml: string; pngDataUrl: string }> {
  const template = templateMap[type]
  let svg = replacePlaceholders(template, data)
  svg = await inlineImages(svg)

  const pngDataUrl = await svgToPng(svg)
  return { svgHtml: svg, pngDataUrl }
}

/**
 * 下载 PNG 图片
 */
export function downloadPng(dataUrl: string, filename = 'poster.png') {
  const link = document.createElement('a')
  link.href = dataUrl
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}
